import styles from "./SettlementTable.module.scss"
import SettlementTableRow from "./SettlementTableRow"

import {
	getFragmentData,
	graphql,
	type FragmentType,
} from "@/company_admin/gql/__generated__"
import QuestionExplanationIcon from "@/lib/components/ui/QuetionExplanationIcon/QuestionExplanationIcon"

// --- Fragment ---

export const settlementTableFragment = graphql(/* GraphQL */ `
	fragment SettlementTableFields on Settlement {
		id
		yearMonth {
			year
			month
		}
		status
		totalPaidCancelFee
		cancelFeeCommissionExclTax
		totalRereservationAmount
		rereservationCommissionExclTax
		souhlCarryoverAmount
		companyCarryoverAmount
		totalAdjustment {
			amount
			isPayout
		}
		finalAmount {
			amount
			isPayout
		}
		transferFee
		companyPaymentDue
		hasUnconfirmedRereservation
	}
`)

type Props = {
	settlements: readonly FragmentType<typeof settlementTableFragment>[]
	isSouhlAdmin: boolean
	onRecreate: (settlementId: string) => void
	onOpenRereservationStatusFix: (settlementId: string) => void
	onUpdateStatus: (settlementId: string, targetStatus: string) => void
	onSendEmail: (settlementId: string) => void
	onExcelDownload: (settlementId: string) => Promise<void>
	downloadingSettlementId: string | null
}

const SettlementTable = ({
	settlements: settlementsRaw,
	isSouhlAdmin,
	onRecreate,
	onOpenRereservationStatusFix,
	onUpdateStatus,
	onSendEmail,
	onExcelDownload,
	downloadingSettlementId,
}: Props) => {
	const settlements = getFragmentData(settlementTableFragment, settlementsRaw)
	// 13 data columns + excel + warning + menu(admin)
	const colSpan = isSouhlAdmin ? 16 : 15

	return (
		<div className={styles.table_container}>
			<table className={styles.table}>
				<thead>
					<tr className={styles.header}>
						<th>成果月</th>
						<th className={styles.header_center}>
							<div className={styles.header_cell_content_center}>
								<span className={styles.header_cell_text}>
									御社
									<br />
									お支払期限
								</span>
								<QuestionExplanationIcon contentMessage="「支払い種別」が「御社支払い」の場合に、期限が記載されます。請求書 PDF に記載の振込先へ、お振込をお願いします。" />
							</div>
						</th>
						<th className={styles.header_amount}>
							<span className={styles.header_cell_text}>
								請求管理機能
								<br />
								決済金額
							</span>
						</th>
						<th className={styles.header_amount}>
							<div className={styles.header_cell_with_tooltip}>
								<span className={styles.header_cell_text}>
									請求管理機能
									<br />
									サービス手数料(税抜)
								</span>
								<QuestionExplanationIcon contentMessage="請求管理機能 決済金額 × 請求管理機能サービス手数料率 (税抜)" />
							</div>
						</th>
						<th className={styles.header_amount}>
							<span className={styles.header_cell_text}>
								離脱顧客機能
								<br />
								再予約金額
							</span>
						</th>
						<th className={styles.header_amount}>
							<div className={styles.header_cell_with_tooltip}>
								<span className={styles.header_cell_text}>
									離脱顧客獲得機能
									<br />
									サービス手数料(税抜)
								</span>
								<QuestionExplanationIcon contentMessage="離脱顧客機能 再予約金額 × 離脱顧客機能サービス手数料率 (税抜)" />
							</div>
						</th>
						<th className={styles.header_amount}>
							<span className={styles.header_cell_text}>
								前月SOUHL
								<br />
								未振込金額
							</span>
						</th>
						<th className={styles.header_amount}>
							<span className={styles.header_cell_text}>
								前月御社
								<br />
								未払い金額
							</span>
						</th>
						<th className={styles.header_amount}>
							<div className={styles.header_cell_with_tooltip}>
								<span className={styles.header_cell_text}>調整金額</span>
								<QuestionExplanationIcon contentMessage="返金対応等で発生する調整金額。内訳は調整タブをご覧ください。" />
							</div>
						</th>
						<th className={styles.header_amount}>
							<div className={styles.header_cell_with_tooltip}>
								<span className={styles.header_cell_text}>振込手数料</span>
								<QuestionExplanationIcon contentMessage="「支払い種別」が「SOUHL 振込」の場合 220 円。御社支払いの場合 0 円" />
							</div>
						</th>
						<th className={styles.header_amount}>
							<div className={styles.header_cell_with_tooltip}>
								<span className={styles.header_cell_text}>最終金額</span>
								<QuestionExplanationIcon contentMessage="「請求管理機能 決済金額 - (請求管理機能 サービス手数料 (税抜) + 離脱顧客機能 サービス手数料 (税抜)) × 1.1 + RefundHub 割引 × 1.1 ± 調整金額 - 振込手数料」の絶対値 (離脱顧客機能 サービス手数料は値引き前の再予約金額ベース)" />
							</div>
						</th>
						<th>支払い種別</th>
						<th>ステータス</th>
						<th className={styles.header_excel} />
						<th className={styles.header_warning} />
						{isSouhlAdmin && <th className={styles.header_menu} />}
					</tr>
				</thead>
				<tbody>
					{settlements.length === 0 ? (
						<tr className={styles.empty_body}>
							<td colSpan={colSpan}>明細はありません</td>
						</tr>
					) : (
						settlements.map((s) => (
							<SettlementTableRow
								key={s.id}
								settlement={s}
								isSouhlAdmin={isSouhlAdmin}
								onRecreate={onRecreate}
								onOpenRereservationStatusFix={onOpenRereservationStatusFix}
								onUpdateStatus={onUpdateStatus}
								onSendEmail={onSendEmail}
								onExcelDownload={onExcelDownload}
								isExcelDownloading={downloadingSettlementId === s.id}
							/>
						))
					)}
				</tbody>
			</table>
		</div>
	)
}

export default SettlementTable
